"use client";

import { useEffect, useRef, useState } from "react";

// The byline verb: server-renders as "Written" (a real, crawlable byline and
// the safe state for no-JS / reduced-motion), then once the author card scrolls
// into view it backspaces character-by-character and retypes as "Compiled".
const FROM = "Written";
const TO = "Compiled";
const STEP_MS = 130; // per character
const START_DELAY_MS = 450; // pause after it enters view

export function AnimatedByline() {
  const [text, setText] = useState(FROM);
  const [animating, setAnimating] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      // jump to the end state, no motion (deferred so it's not a sync setState)
      timers.push(setTimeout(() => setText(TO), 0));
      return () => timers.forEach(clearTimeout);
    }

    const run = () => {
      setAnimating(true);
      let t = START_DELAY_MS;
      // backspace "Written"
      for (let i = FROM.length - 1; i >= 0; i--) {
        const slice = FROM.slice(0, i);
        timers.push(setTimeout(() => setText(slice), t));
        t += STEP_MS;
      }
      // type "Compiled"
      for (let i = 1; i <= TO.length; i++) {
        const slice = TO.slice(0, i);
        timers.push(setTimeout(() => setText(slice), t));
        t += STEP_MS;
      }
      timers.push(setTimeout(() => setAnimating(false), t));
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            observer.disconnect();
            run();
          }
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    // Assistive tech gets a stable read of the verb as hidden text (aria-label
    // isn't allowed on a plain span); the live-updating glyphs are hidden so the
    // typewriter churn isn't announced.
    <span ref={ref}>
      <span className="sr-only">{FROM}</span>
      <span aria-hidden="true">{text}</span>
      {animating && (
        <span
          aria-hidden="true"
          className="ml-0.5 inline-block w-px animate-pulse bg-current align-baseline"
          style={{ height: "1em" }}
        />
      )}
    </span>
  );
}
